import React, { useState, useEffect, useMemo, useRef } from 'react';
import { usePreviewPng, uploadShareCard, saveImageBlob } from '@/utils/shareCard';
import { X, Download, Share2, Link2, Check, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { shareViaNative } from '@/utils/nativeShare';

import { useTranslation } from 'react-i18next';

/**
 * ShareRecapTopicModal — themed share-card modal for a SINGLE recap topic.
 *
 * The preview + server-rendered card both mirror the in-app
 * GlassSummaryModal look on RecapPage — an indigo glass-morphic panel
 * with badge → topic label → highlight card → key points → takeaways,
 * so the recipient's view (SPA page or social unfurl) matches exactly
 * what you see when you tap a bubble in the recap.
 */
function RecapTopicShareCardPreview({ label, highlight, keyPoints, takeaways, dateRange, accent, orientation }) {
  const {
    t
  } = useTranslation();

  const acc = accent || 'rgba(99, 102, 241, 0.7)';
  const isPortrait = orientation === 'portrait';
  // Min 4:5 (portrait) / 1200:630 (landscape); grows if content is longer so nothing clips.
  return (
    <div style={{ background: '#0f172a', padding: 12 }}>
      <div style={{
        width: '100%',
        aspectRatio: isPortrait ? '4 / 5' : '1200 / 630',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          flex: 1,
          background: '#0f172a',
          fontFamily: "'Helvetica Neue', Arial, sans-serif",
          borderRadius: 12,
          padding: isPortrait ? '18px 14px' : '20px 22px',
          display: 'flex', alignItems: 'stretch', justifyContent: 'stretch',
        }}>
          <div
            style={{
              position: 'relative',
              flex: 1,
              borderRadius: isPortrait ? 22 : 20,
              overflow: 'hidden',
              background: `linear-gradient(160deg,
                ${acc.replace('0.7', '0.7')} 0%,
                ${acc.replace('0.7', '0.5')} 40%,
                ${acc.replace('0.7', '0.6')} 100%)`,
              border: '1px solid rgba(255,255,255,0.25)',
              padding: isPortrait ? '18px 16px 14px' : '16px 18px 14px',
              boxShadow: '0 24px 48px -12px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.3)',
              display: 'flex', flexDirection: 'column',
            }}
          >
            {/* Top glass streak */}
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0,
              height: isPortrait ? 74 : 56,
              background: 'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 100%)',
              pointerEvents: 'none',
              borderRadius: `${isPortrait ? 22 : 20}px ${isPortrait ? 22 : 20}px 0 0`,
            }} />

            {/* Badge */}
            <div style={{
              position: 'relative',
              alignSelf: 'flex-start',
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '4px 10px', borderRadius: 999,
              background: 'rgba(255,255,255,0.18)', color: '#fff',
              fontSize: isPortrait ? 8.5 : 9,
              fontWeight: 700, letterSpacing: '0.15em',
              textTransform: 'uppercase', marginBottom: 8,
              border: '1px solid rgba(255,255,255,0.35)',
            }}>
              {t('generated.sharerecaptopicmodal_weekly_recap_317b')}{dateRange ? `  ·  ${dateRange}` : ''}
            </div>

            {/* Topic label */}
            <h2 style={{
              position: 'relative',
              fontSize: isPortrait ? 26 : 20, fontWeight: 800, lineHeight: 1.12,
              color: '#fff', margin: '0 0 10px',
              textShadow: '0 2px 4px rgba(0,0,0,0.25)',
            }}>
              {label}
            </h2>

            {/* Highlight card */}
            {highlight && (
              <div style={{
                position: 'relative',
                background: 'rgba(255,255,255,0.13)',
                border: '1px solid rgba(255,255,255,0.18)',
                borderRadius: 12,
                padding: '9px 12px',
                marginBottom: 10,
              }}>
                <div style={{
                  fontSize: 8, fontWeight: 700, letterSpacing: '0.15em',
                  color: 'rgba(255,255,255,0.7)', marginBottom: 3,
                  textTransform: 'uppercase',
                }}>
                  {t('generated.sharerecaptopicmodal_highlight_d2e0')}
                </div>
                <p style={{
                  margin: 0, color: '#fff',
                  fontSize: isPortrait ? 13 : 11, lineHeight: 1.35,
                  textShadow: '0 1px 2px rgba(0,0,0,0.2)',
                }}>
                  {highlight}
                </p>
              </div>
            )}

            {/* Key points */}
            {keyPoints && keyPoints.length > 0 && (
              <div style={{ position: 'relative', marginBottom: isPortrait ? 8 : 4 }}>
                <div style={{
                  fontSize: 8, fontWeight: 700, letterSpacing: '0.15em',
                  color: 'rgba(255,255,255,0.7)', marginBottom: 5,
                  textTransform: 'uppercase',
                }}>
                  {t('generated.sharerecaptopicmodal_key_points_6f98')}
                </div>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  {keyPoints.slice(0, isPortrait ? 4 : 3).map((p, i) => (
                    <li key={i} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 7,
                      marginBottom: 4,
                      color: '#fff', fontSize: isPortrait ? 12 : 10.5, lineHeight: 1.35,
                    }}>
                      <span style={{
                        width: 4, height: 4, borderRadius: 999,
                        background: 'rgba(255,255,255,0.85)',
                        marginTop: 6, flexShrink: 0,
                      }} />
                      <span style={{ textShadow: '0 1px 2px rgba(0,0,0,0.15)' }}>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Takeaways — portrait only */}
            {isPortrait && takeaways && takeaways.length > 0 && (
              <div style={{
                position: 'relative',
                background: 'rgba(255,255,255,0.13)',
                border: '1px solid rgba(255,255,255,0.18)',
                borderRadius: 12,
                padding: '9px 12px',
                marginTop: 4,
              }}>
                <div style={{
                  fontSize: 8, fontWeight: 700, letterSpacing: '0.15em',
                  color: 'rgba(255,255,255,0.7)', marginBottom: 4,
                  textTransform: 'uppercase',
                }}>
                  {t('generated.sharerecaptopicmodal_takeaways_839b')}
                </div>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  {takeaways.slice(0, 3).map((t, i) => (
                    <li key={i} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 7,
                      marginBottom: 3,
                      color: '#fff', fontSize: 12, lineHeight: 1.35,
                    }}>
                      <span style={{ fontSize: 11 }}>✦</span>
                      <span style={{ textShadow: '0 1px 2px rgba(0,0,0,0.15)' }}>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Spacer pushes footer to the bottom */}
            <div style={{ flex: 1 }} />

            {/* Footer */}
            <div style={{
              position: 'relative',
              paddingTop: 8,
              borderTop: '1px solid rgba(255,255,255,0.18)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <img
                  src="/logo.png"
                  alt={t('generated.sharerecaptopicmodal_eili_b095')}
                  style={{ width: isPortrait ? 20 : 18, height: isPortrait ? 20 : 18, objectFit: 'contain' }}
                  crossOrigin="anonymous"
                />
                <span style={{
                  fontFamily: "'Playwrite ES Deco', cursive",
                  fontSize: isPortrait ? 12 : 11, fontWeight: 700,
                  color: '#fff', marginTop: 2,
                }}>
                  {t('generated.sharerecaptopicmodal_eili_b095')}
                </span>
              </div>
              <span style={{ fontSize: isPortrait ? 9 : 8, color: 'rgba(255,255,255,0.65)' }}>askeili.com</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ShareRecapTopicModal({
  label,
  highlight,
  keyPoints,
  takeaways,
  weekStart,
  weekEnd,
  bubbleColor,
  onClose,
}) {
  const {
    t
  } = useTranslation();

  const [linkCopied, setLinkCopied] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [creating, setCreating] = useState(true);
  const [busy, setBusy] = useState(false);

  const accent = bubbleColor || 'rgba(99, 102, 241, 0.7)';
  const apiBase = `${window.location.origin}/api`;
  const dateRange = weekStart && weekEnd ? `${weekStart} – ${weekEnd}` : '';

  // Match the sender's device orientation so the shared card looks like
  // the view they're seeing when they hit Share. Read once at mount so a
  // mid-share rotation doesn't confuse the recipient's unfurl.
  const orientation = useMemo(() => {
    if (typeof window === 'undefined') return 'landscape';
    return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
  }, []);

  // Mint the share record once on mount. The glass "card view" look is
  // the ONLY presentation; we send the topic's bubble color + the sender's
  // orientation so the server-rendered PNG mirrors the sender's device.
  useEffect(() => {
    let alive = true;
    setCreating(true);
    axios
      .post(`${apiBase}/recap/topic-share`,
        {
          label,
          highlight: highlight || '',
          key_points: keyPoints || [],
          takeaways: takeaways || [],
          week_start: weekStart || '',
          week_end: weekEnd || '',
          bubble_color: accent,
          orientation,
        },
        { withCredentials: true, timeout: 8000 })
      .then((r) => { if (alive) setShareInfo(r.data); })
      .catch(() => { if (alive) toast.error(t('generated.sharerecaptopicmodal_could_not_create_1cbd')); })
      .finally(() => { if (alive) setCreating(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shareUrl = shareInfo
    ? `${window.location.origin}/api/og/share/${shareInfo.share_id}`
    : null;

  // The official image is a capture of the live preview, so they always match.
  const cardRef = useRef(null);
  const pngBlob = usePreviewPng(cardRef, orientation, orientation === 'portrait' ? 1080 : 1200);
  const syncCard = () => { if (shareInfo) uploadShareCard(shareInfo.share_id, pngBlob); };

  const handleShareImage = async () => {
    if (!pngBlob) return;
    setBusy(true);
    syncCard();
    try {
      const file = new File([pngBlob], `eili-recap-topic-${Date.now()}.png`, { type: 'image/png' });
      const result = await shareViaNative({
        title: `${label} · My Weekly Recap on Eili`,
        text: `${label}${dateRange ? ` · ${dateRange}` : ''}`,
        url: shareUrl,
        file,
      });
      if (!result?.aborted && !result?.shared) {
        toast.info(t('generated.sharerecaptopicmodal_use_save_or_d0dd'));
      }
    } catch {
      toast.error(t('generated.sharerecaptopicmodal_could_not_open_21f3'));
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = () => saveImageBlob(pngBlob, `eili-recap-topic-${Date.now()}.png`);

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    syncCard();
    try {
      await navigator.clipboard.writeText(shareUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      toast.error(t('generated.sharerecaptopicmodal_could_not_copy_f4d7'));
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      data-testid="share-recap-topic-modal"
    >
      <div
        className="relative w-full max-w-lg flex flex-col rounded-2xl overflow-hidden"
        style={{
          background: 'var(--theme-surface, #1e293b)',
          border: '1px solid var(--glass-border, #334155)',
          maxHeight: 'min(90vh, calc(var(--visual-vh, 100vh) - 32px))',
        }}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b"
             style={{ borderColor: 'var(--glass-border, #334155)' }}>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" style={{ color: 'var(--theme-accent)' }} />
            <h3 className="text-base font-semibold" style={{ color: 'var(--theme-text)' }}>
              {t('generated.sharerecaptopicmodal_share_this_topic_2527')}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full transition hover:bg-white/10"
            data-testid="share-recap-topic-close"
          >
            <X className="h-4 w-4" style={{ color: 'var(--theme-textSecondary)' }} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {/* Preview — glass card view, matches the in-app opened bubble */}
          <div>
            <p className="text-xs font-medium uppercase tracking-wider mb-3"
               style={{ color: 'var(--theme-textSecondary)' }}>
              {t('generated.sharerecaptopicmodal_preview_bf0e')} <span className="lowercase opacity-70">· {orientation}</span>
            </p>
            <div
              className="rounded-xl overflow-hidden mx-auto"
              style={{
                border: '1px solid var(--glass-border)',
                maxWidth: orientation === 'portrait' ? 320 : '100%',
              }}
              data-testid="share-recap-topic-preview"
            >
              <div ref={cardRef}>
              <RecapTopicShareCardPreview
                label={label}
                highlight={highlight}
                keyPoints={keyPoints}
                takeaways={takeaways}
                dateRange={dateRange}
                accent={accent}
                orientation={orientation}
              />
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t flex flex-wrap gap-2.5"
             style={{ borderColor: 'var(--glass-border, #334155)' }}>
          <Button
            onClick={handleShareImage}
            disabled={busy || !pngBlob || creating}
            className="flex-1 min-w-[120px] rounded-xl py-4 font-medium gap-2"
            style={{ background: accent.replace('0.7', '1'), color: '#fff' }}
            data-testid="share-recap-topic-image-btn"
          >
            {!pngBlob ? (
              <>
                <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('generated.sharerecaptopicmodal_preparing_c559')}
              </>
            ) : (
              <>
                <Share2 className="h-4 w-4" />
                {busy ? 'Sharing…' : 'Share Image'}
              </>
            )}
          </Button>
          <Button
            onClick={handleDownload}
            disabled={!pngBlob}
            variant="outline"
            className="rounded-xl py-4 font-medium gap-2"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
            data-testid="share-recap-topic-download-btn"
          >
            <Download className="h-4 w-4" />
            {t('generated.sharerecaptopicmodal_save_f126')}
          </Button>
          <Button
            onClick={handleCopyLink}
            disabled={!shareUrl || !pngBlob}
            variant="outline"
            className="rounded-xl py-4 font-medium gap-2"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--theme-text)' }}
            data-testid="share-recap-topic-copy-link-btn"
          >
            {linkCopied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
            {linkCopied ? 'Copied' : 'Link'}
          </Button>
        </div>
      </div>
    </div>
  );
}
